import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, CheckCircle, XCircle, Clock, FileText, DollarSign, AlertTriangle, User, Mail, MessageSquare, Trash2 } from 'lucide-react';

const STEP_ORDER = ['received','extracting','extracted','review_required','confirmed','matching','matched','pending_approval','ops_approved','settlements_approved','finance_approved','actualized','posted_to_d365','closed'];

function StatusTimeline({ currentStatus }) {
  const displaySteps = ['Received','Extracted','Confirmed','Matched','Ops Approved','Settlements','Finance','Actualized','Closed'];
  const statusMap = { 'Received': 0, 'Extracted': 2, 'Confirmed': 4, 'Matched': 6, 'Ops Approved': 8, 'Settlements': 9, 'Finance': 10, 'Actualized': 11, 'Closed': 13 };
  const currentIdx = STEP_ORDER.indexOf(currentStatus);

  return (
    <div className="flex items-center gap-0 overflow-x-auto py-2">
      {displaySteps.map((step, i) => {
        const stepIdx = statusMap[step];
        const done = currentIdx >= stepIdx;
        const active = currentIdx === stepIdx;
        return (
          <div key={step} className="flex items-center">
            <div className={`flex flex-col items-center min-w-[70px]`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                done ? 'bg-[#16A34A] border-[#16A34A] text-white' :
                active ? 'bg-white border-[#007AFF] text-[#007AFF]' :
                'bg-white border-slate-200 text-slate-300'
              }`}>
                {done ? <CheckCircle className="h-3 w-3" /> : i + 1}
              </div>
              <span className={`text-[9px] mt-1 text-center uppercase tracking-wider ${done ? 'text-slate-700 font-semibold' : 'text-slate-400'}`}>
                {step}
              </span>
            </div>
            {i < displaySteps.length - 1 && (
              <div className={`w-6 h-0.5 ${done ? 'bg-[#16A34A]' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/invoices/${id}`).then(r => setInvoice(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-8"><Skeleton className="h-96" /></div>;
  if (!invoice) return <div className="p-8 text-center text-slate-400">Invoice not found</div>;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button" className="rounded-sm">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-black tracking-tighter text-slate-950">{invoice.invoice_number}</h1>
          <p className="text-sm text-slate-500">{invoice.vendor_name}</p>
        </div>
        <Badge variant="outline" className="rounded-sm text-xs uppercase tracking-wider px-3 py-1">
          {invoice.status?.replace(/_/g, ' ')}
        </Badge>
        {invoice.verification_status === 'verified_clean' && (
          <Badge className="rounded-sm text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="verified-clean-badge">
            <CheckCircle className="h-3 w-3 mr-1" /> Verified Clean
          </Badge>
        )}
        {invoice.verification_status === 'discrepancies_found' && (
          <Badge className="rounded-sm text-[10px] px-2 py-0.5 bg-red-100 text-red-800 border border-red-200" data-testid="discrepancies-found-badge">
            <AlertTriangle className="h-3 w-3 mr-1" /> Discrepancies Found
          </Badge>
        )}
        {(!invoice.verification_status || invoice.verification_status === 'not_verified') && (
          <Badge className="rounded-sm text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200" data-testid="not-verified-badge">
            Not Verified
          </Badge>
        )}
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 text-[10px]"
          data-testid="invoice-delete-button"
          onClick={async () => {
            if (!window.confirm(`Delete invoice "${invoice.invoice_number}"? This will remove all line items, extraction results, approvals, and discrepancies. This cannot be undone.`)) return;
            try {
              await api.delete(`/invoices/${id}`);
              navigate('/invoices');
            } catch (e) {
              console.error(e);
              alert('Failed to delete invoice');
            }
          }}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          Delete
        </Button>
      </div>

      {/* Status Timeline */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="py-4 px-6">
          <StatusTimeline currentStatus={invoice.status} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-8 space-y-6">
          {/* Key Info */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Invoice Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Trade ID', value: invoice.trade_id || '—', mono: true },
                  { label: 'Date', value: invoice.invoice_date || '—' },
                  { label: 'Amount', value: `${invoice.currency || 'USD'} ${parseFloat(invoice.total_amount || 0).toLocaleString()}`, mono: true },
                  { label: 'Confidence', value: invoice.confidence || 'N/A' },
                  { label: 'Payment Terms', value: invoice.payment_terms || '—' },
                  { label: 'Trade ID Validated', value: invoice.trade_id_validated ? 'Yes' : 'No' },
                ].map(({ label, value, mono }) => (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</p>
                    <p className={`text-sm text-slate-900 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Line Items ({invoice.line_items?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="line-items-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Cost Type</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Description</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Amount</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.line_items || []).map(li => (
                    <tr key={li.id} className="border-b border-slate-50">
                      <td className="px-5 py-3">
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                          {li.cost_type?.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{li.description}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-900">{li.currency} {parseFloat(li.amount).toLocaleString()}</td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${
                          ['very_high','high'].includes(li.confidence) ? 'border-green-300 text-green-700' :
                          li.confidence === 'medium' ? 'border-amber-300 text-amber-700' :
                          'border-red-300 text-red-700'
                        }`}>{li.confidence}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {/* Right: Sidebar info */}
        <div className="lg:col-span-4 space-y-6">
          {/* Approvals */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Approval Chain</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(invoice.approvals || []).map(a => (
                <div key={a.id} className="flex items-center gap-3 p-2 border border-slate-100 rounded-sm">
                  {a.status === 'approved' ? <CheckCircle className="h-4 w-4 text-green-500" /> :
                   a.status === 'rejected' ? <XCircle className="h-4 w-4 text-red-500" /> :
                   <Clock className="h-4 w-4 text-amber-500" />}
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-700 uppercase">{a.approval_tier}</p>
                    <p className="text-[10px] text-slate-400">{a.approver_name || 'Pending'}</p>
                  </div>
                  <Badge variant="outline" className={`rounded-sm text-[10px] ${
                    a.status === 'approved' ? 'border-green-300 text-green-700' :
                    a.status === 'rejected' ? 'border-red-300 text-red-700' :
                    'border-amber-300 text-amber-700'
                  }`}>{a.status}</Badge>
                </div>
              ))}
              {(!invoice.approvals || invoice.approvals.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-4">No approvals yet</p>
              )}
            </CardContent>
          </Card>

          {/* Discrepancies */}
          {invoice.discrepancies?.length > 0 && (() => {
            const totalVendor = invoice.discrepancies.reduce((s, d) => s + (d.vendor_amount || 0), 0);
            const totalVariance = invoice.discrepancies.reduce((s, d) => s + (d.variance || 0), 0);
            const correctTotal = invoice.total_amount - totalVariance;
            return (
            <Card className="bg-white border border-red-200 rounded-sm shadow-none" data-testid="discrepancies-section">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <CardTitle className="text-sm font-semibold text-slate-800">
                    Discrepancies ({invoice.discrepancies.length})
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto text-[10px] h-6 rounded-sm"
                    data-testid="redetect-btn"
                    onClick={async () => {
                      try {
                        await api.post(`/invoices/${id}/redetect-discrepancies`);
                        window.location.reload();
                      } catch (e) { console.error(e); }
                    }}
                  >
                    Re-detect
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Overcharge Summary */}
                {totalVariance > 0 && (
                  <div className="bg-red-50 border border-red-300 rounded-sm p-3 mb-2" data-testid="overcharge-summary">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-red-500 font-bold">Total Overcharge Detected</div>
                        <div className="text-xl font-black text-red-700">${totalVariance.toLocaleString()}</div>
                      </div>
                      <div className="text-right text-[11px] text-slate-600">
                        <div>Invoice Claims: <strong>${Number(invoice.total_amount).toLocaleString()}</strong></div>
                        <div>Correct Amount: <strong className="text-green-700">${correctTotal.toLocaleString()}</strong></div>
                      </div>
                    </div>
                  </div>
                )}
                {invoice.discrepancies.map(d => {
                  const sevColor = d.severity === 'high' ? 'border-red-300 bg-red-50' :
                    d.severity === 'medium' ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50';
                  const sevText = d.severity === 'high' ? 'text-red-700 bg-red-100' :
                    d.severity === 'medium' ? 'text-amber-700 bg-amber-100' : 'text-slate-600 bg-slate-100';

                  const typeLabels = {
                    'rate_mismatch': 'Rate Mismatch',
                    'laytime_mismatch': 'Laytime Mismatch',
                    'unauthorized_charge': 'Unauthorized Charge',
                    'amount_anomaly': 'Amount Anomaly',
                    'excess_days': 'Excess Days',
                    'freight_schedule_mismatch': 'Freight Schedule Mismatch',
                    'freight_total_mismatch': 'Freight Total Mismatch',
                  };

                  const evidence = d.evidence_json ? (typeof d.evidence_json === 'string' ? JSON.parse(d.evidence_json) : d.evidence_json) : {};
                  const isOvercharge = d.variance > 0;

                  return (
                    <div key={d.id} className={`p-3 border rounded-sm ${sevColor}`} data-testid={`discrepancy-${d.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${sevText}`}>
                          {d.severity}
                        </span>
                        <span className="text-xs font-semibold text-slate-800">
                          {typeLabels[d.discrepancy_type] || (d.discrepancy_type || '').replace(/_/g, ' ')}
                        </span>
                        {d.variance > 0 && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm text-red-800 bg-red-200">
                            OVERCHARGE
                          </span>
                        )}
                        <Badge variant="outline" className="ml-auto text-[9px] rounded-sm">{d.status}</Badge>
                      </div>

                      {/* Rate Mismatch Details */}
                      {d.discrepancy_type === 'rate_mismatch' && evidence.invoice_rate_per_day && (
                        <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                          <div className="bg-white/70 p-1.5 rounded-sm border border-red-100">
                            <div className="text-slate-400 text-[9px] uppercase">Invoice {evidence.charge_type || 'Demurrage'} Rate</div>
                            <div className="font-bold text-red-700">${Number(evidence.invoice_rate_per_day).toLocaleString()}/day</div>
                          </div>
                          <div className="bg-white/70 p-1.5 rounded-sm border border-green-100">
                            <div className="text-slate-400 text-[9px] uppercase">CP Rate</div>
                            <div className="font-bold text-green-700">${Number(evidence.cp_rate_per_day).toLocaleString()}/day</div>
                          </div>
                          <div className="bg-white/70 p-1.5 rounded-sm border border-red-100">
                            <div className="text-slate-400 text-[9px] uppercase">Overcharge</div>
                            <div className="font-bold text-red-700">+${Number(evidence.rate_difference_per_day).toLocaleString()}/day</div>
                          </div>
                        </div>
                      )}

                      {/* Laytime Mismatch Details */}
                      {d.discrepancy_type === 'laytime_mismatch' && evidence.invoice_laytime_hours && (
                        <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                          <div className="bg-white/70 p-1.5 rounded-sm border border-red-100">
                            <div className="text-slate-400 text-[9px] uppercase">Invoice Laytime</div>
                            <div className="font-bold text-red-700">{evidence.invoice_laytime_hours}h</div>
                          </div>
                          <div className="bg-white/70 p-1.5 rounded-sm border border-green-100">
                            <div className="text-slate-400 text-[9px] uppercase">CP Laytime</div>
                            <div className="font-bold text-green-700">{evidence.cp_laytime_hours}h {evidence.cp_laytime_basis}</div>
                          </div>
                          <div className="bg-white/70 p-1.5 rounded-sm border border-red-100">
                            <div className="text-slate-400 text-[9px] uppercase">Extra Cost</div>
                            <div className="font-bold text-red-700">${Number(evidence.extra_cost_at_cp_rate).toLocaleString()}</div>
                          </div>
                        </div>
                      )}

                      {/* Excess Days Details */}
                      {d.discrepancy_type === 'excess_days' && evidence.days_claimed && (
                        <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                          <div className="bg-white/70 p-1.5 rounded-sm border border-red-100">
                            <div className="text-slate-400 text-[9px] uppercase">Days Claimed</div>
                            <div className="font-bold text-red-700">{evidence.days_claimed}</div>
                          </div>
                          <div className="bg-white/70 p-1.5 rounded-sm border border-green-100">
                            <div className="text-slate-400 text-[9px] uppercase">CP Max Days</div>
                            <div className="font-bold text-green-700">{evidence.cp_maximum_days}</div>
                          </div>
                          <div className="bg-white/70 p-1.5 rounded-sm border border-red-100">
                            <div className="text-slate-400 text-[9px] uppercase">Excess ({evidence.excess_days} days)</div>
                            <div className="font-bold text-red-700">${Number(evidence.excess_cost_at_cp_rate).toLocaleString()}</div>
                          </div>
                        </div>
                      )}

                      {/* Amount row */}
                      {d.vendor_amount > 0 && (
                        <div className="flex gap-4 text-[11px] text-slate-600 mt-2">
                          <span>Vendor Claims: <strong className="text-red-700">${Number(d.vendor_amount).toLocaleString()}</strong></span>
                          {d.calculated_amount > 0 && <span>Correct Amount: <strong className="text-green-700">${Number(d.calculated_amount).toLocaleString()}</strong></span>}
                          {d.variance !== 0 && (
                            <span className={isOvercharge ? "text-red-600" : "text-green-600"}>
                              Variance: <strong>{isOvercharge ? '+' : ''}${Number(d.variance).toLocaleString()}</strong>
                            </span>
                          )}
                        </div>
                      )}

                      {d.recommended_action && (
                        <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">{d.recommended_action}</p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            );
          })()}

          {/* Audit Log */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Activity Log</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(invoice.audit_log || []).slice(0, 10).map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                  <div>
                    <span className="text-slate-600">{a.action?.replace(/_/g, ' ')}</span>
                    {a.user_name && <span className="text-slate-400 ml-1">by {a.user_name}</span>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* AI Extraction Results */}
          {invoice.extraction_results?.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">AI Extraction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.extraction_results.map(e => (
                  <div key={e.id} className="p-2 border border-slate-100 rounded-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-slate-700">{e.model_used}</span>
                      <Badge variant="outline" className="rounded-sm text-[10px] border-green-300 text-green-700 bg-green-50">
                        {e.overall_confidence ? `${parseFloat(e.overall_confidence).toFixed(0)}%` : 'N/A'}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-slate-400">{e.processing_time_ms}ms latency</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* D365 Sync History */}
          {invoice.d365_sync_history?.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">D365 Sync</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.d365_sync_history.map(s => (
                  <div key={s.id} className="flex items-center gap-2 p-2 border border-slate-100 rounded-sm">
                    {s.success ? <CheckCircle className="h-3 w-3 text-green-500" /> : <XCircle className="h-3 w-3 text-red-500" />}
                    <div className="flex-1">
                      <p className="text-xs font-medium text-slate-700">{s.sync_type?.replace(/_/g, ' ')}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{s.d365_reference_id}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Vendor Communications */}
          {invoice.vendor_communications?.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="vendor-comms-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  Vendor Communications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.vendor_communications.map(vc => (
                  <div key={vc.id} className="p-2 border border-slate-100 rounded-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                        {vc.communication_type}
                      </Badge>
                      <Badge variant="outline" className={`rounded-sm text-[9px] ${
                        vc.status === 'sent' ? 'border-green-300 text-green-700 bg-green-50' : 'border-slate-300 text-slate-600'
                      }`}>
                        {vc.status}
                      </Badge>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{vc.subject}</p>
                    <p className="text-[10px] text-slate-400">To: {vc.recipient_email}</p>
                    {vc.sent_at && (
                      <p className="text-[10px] text-slate-400 font-mono">
                        {new Date(vc.sent_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* AI Feedback Corrections */}
          {invoice.ai_feedback?.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="ai-feedback-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                  AI Feedback ({invoice.ai_feedback.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.ai_feedback.map(fb => (
                  <div key={fb.id} className="p-2 border border-slate-100 rounded-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                        {fb.field_name?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {fb.feedback_timestamp ? new Date(fb.feedback_timestamp).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <div>
                        <span className="text-[10px] text-slate-400">From: </span>
                        <span className="font-mono text-red-600 line-through">{fb.old_value || '\u2014'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400">To: </span>
                        <span className="font-mono text-green-700">{fb.new_value || '\u2014'}</span>
                      </div>
                    </div>
                    {fb.corrected_by && (
                      <p className="text-[10px] text-slate-400">by {fb.corrected_by}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
